import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { AuthStatus } from 'constants/auth';
import {
  FollowStatusesEnum,
  useGetPlayerProfileLazyQuery,
  useGetPlayerProfilePublicLazyQuery,
  useUpsertFollowerConnectionMutation,
} from 'types/generated/client';
import { useAuthModals } from 'hooks/useAuthModals';
import { useViewer } from 'hooks/useViewer';
import type { PlayerProfileView } from './types';
import { updateCachePlayerProfileFollowing } from './updateCachePlayerProfileFollowing';
import { toPlayerProfileView } from './utils';

interface Result {
  profile: PlayerProfileView | null;
  isLoading: boolean;
  isViewerProfile: boolean;
  /** "Pin to Favorites" is the same follow edge the V1 profile used. */
  isFollowing: boolean;
  isFollowLoading: boolean;
  toggleFollow: () => void;
  /** True only for a signed-in visitor who has played with them and hasn't reviewed yet. */
  canWriteReview: boolean;
  refetch: () => void;
}

/**
 * Anonymous and signed-in viewers hit different queries: only the signed-in one can
 * resolve the follow row. Both are gated on the viewer's status resolving first,
 * because firing before then runs as `anonymous` and caches the wrong answer.
 */
export const usePlayerProfile = (username: string): Result => {
  const viewer = useViewer();
  const { openSignupModal } = useAuthModals();
  const [upsertFollowerMutation, { loading: isFollowLoading }] =
    useUpsertFollowerConnectionMutation();

  // Fixed at mount so refetches count the same "past" games as the first load.
  const [now] = useState(() => new Date().toISOString());
  const [getProfile, { data, loading, called, refetch }] = useGetPlayerProfileLazyQuery();
  const [
    getPublicProfile,
    { data: publicData, loading: publicLoading, called: publicCalled, refetch: refetchPublic },
  ] = useGetPlayerProfilePublicLazyQuery();

  useEffect(() => {
    if (!username || viewer.status === AuthStatus.Loading) return;

    if (viewer.status === AuthStatus.User && viewer.userId) {
      getProfile({
        variables: { username: username.toLowerCase(), viewerId: viewer.userId, now },
      }).catch((error) => Sentry.captureException(error));
    } else {
      getPublicProfile({ variables: { username: username.toLowerCase(), now } }).catch((error) =>
        Sentry.captureException(error),
      );
    }
  }, [username, viewer.status, viewer.userId]);

  const raw = data?.userProfiles?.[0] ?? publicData?.userProfiles?.[0];
  const counts = data ?? publicData;
  const gamesPlayed =
    (counts?.hostedGamesAggregate.aggregate?.count ?? 0) +
    (counts?.joinedGamesAggregate.aggregate?.count ?? 0);

  const profile = useMemo(
    () => (raw ? toPlayerProfileView(raw, gamesPlayed) : null),
    [raw, gamesPlayed],
  );

  const isPublicLoading =
    viewer.status === AuthStatus.Anonymous && (publicLoading || !publicCalled);
  const isAuthedLoading = viewer.status === AuthStatus.User && (loading || !called);
  const isFollowing = data?.userFollows?.[0]?.status === FollowStatusesEnum.Active;

  const authedProfile = data?.userProfiles?.[0];
  const isViewerProfile = !!viewer.userId && !!profile?.id && viewer.userId === profile.id;
  // Both facts only exist on the authed query, so an anonymous viewer can never pass
  // this gate: they have no accepted play request and no review to have written.
  const canWriteReview =
    !isViewerProfile &&
    !!authedProfile &&
    authedProfile.viewerReview.length === 0 &&
    (data?.acceptedPlayRequests?.length ?? 0) > 0;

  const toggleFollow = () => {
    // A logged-out visitor can see the button but has no edge to write; send them
    // to signup rather than failing the mutation.
    if (!viewer.userId || !profile?.id) {
      openSignupModal();
      return;
    }

    const variables = {
      followerUserId: viewer.userId,
      followedUserId: profile.id,
      status: isFollowing ? FollowStatusesEnum.Inactive : FollowStatusesEnum.Active,
    };

    upsertFollowerMutation({
      variables,
      update: profile.username ? updateCachePlayerProfileFollowing(profile.username) : undefined,
      optimisticResponse: {
        __typename: 'mutation_root',
        insertUserFollowsOne: { __typename: 'UserFollows', ...variables },
      },
    }).catch((error) => Sentry.captureException(error));
  };

  return {
    profile,
    isLoading: viewer.status === AuthStatus.Loading || isPublicLoading || isAuthedLoading,
    isViewerProfile,
    isFollowing,
    isFollowLoading,
    toggleFollow,
    canWriteReview,
    refetch: () => {
      if (viewer.status === AuthStatus.User && viewer.userId) {
        refetch?.({ username: username.toLowerCase(), viewerId: viewer.userId });
        return;
      }
      refetchPublic?.({ username: username.toLowerCase() });
    },
  };
};
